import { useEffect, useState } from 'react';
import { apiFetch } from './api';

// Store en memoria del Dashboard (vive mientras la SPA esté abierta). Al
// volver a la pestaña se pinta al instante lo último cargado y solo se vuelve
// a pedir si tiene más de FRESH_MS o si el usuario pulsa Refrescar. Antes cada
// montaje del componente disparaba la carga completa de nuevo.

const FRESH_MS = 5 * 60 * 1000;

let state = { data: null, fetchedAt: 0, loading: false, refreshing: false, error: null, promise: null };
const listeners = new Set();

function setState(patch) {
  state = { ...state, ...patch };
  for (const listener of listeners) listener(state);
}

export function isDashboardFresh() {
  return !!state.data && Date.now() - state.fetchedAt < FRESH_MS;
}

// fresh=true fuerza el recálculo en el Worker (botón Refrescar); force=true
// vuelve a pedir aunque el store esté fresco (revalidación de una caché
// `stale`) sin recalcular en el Worker.
export function loadDashboard({ fresh = false, force = false } = {}) {
  if (state.promise) return state.promise;
  if (!fresh && !force && isDashboardFresh()) return Promise.resolve(state.data);
  const promise = apiFetch(`/api/backoffice/dashboard${fresh ? '?fresh=1' : ''}`)
    .then((data) => {
      setState({ data, fetchedAt: Date.now(), loading: false, error: null, promise: null });
      return data;
    })
    .catch((e) => {
      setState({ loading: false, error: e.message || String(e), promise: null });
      throw e;
    });
  setState({ loading: true, error: null, promise });
  return promise;
}

// Refrescar completo: sincroniza los eventos de Stripe (si el usuario puede
// escribir) y recalcula el stock. Vive en el store, no en el componente:
// si el usuario cambia de pestaña a mitad, la carga sigue y al volver el
// botón sigue en "cargando" hasta que termina.
let refreshPromise = null;

export function refreshDashboard({ syncStripe = false } = {}) {
  if (refreshPromise) return refreshPromise;
  setState({ refreshing: true });
  refreshPromise = (async () => {
    try {
      if (syncStripe) {
        await apiFetch('/api/backoffice/dashboard/sync-stripe', { method: 'POST' }).catch(() => null);
      }
      return await loadDashboard({ fresh: true });
    } catch (_) {
      return null; // el store ya expone el error
    } finally {
      refreshPromise = null;
      setState({ refreshing: false });
    }
  })();
  return refreshPromise;
}

export function useDashboardStore() {
  const [snapshot, setSnapshot] = useState(state);
  useEffect(() => {
    listeners.add(setSnapshot);
    setSnapshot(state);
    return () => listeners.delete(setSnapshot);
  }, []);
  return snapshot;
}
